import type { UserPublic } from '@repo/types/users';
import type { QueryKey } from '@tanstack/react-query';

import { useQueries } from '@tanstack/react-query';

/**
 * One child's slice of an aggregated result, already tagged with the child it
 * belongs to so callers never have to zip two arrays back together.
 */
export interface ChildResource<TData> {
  child: UserPublic;
  data: TData;
}

export interface ChildrenResourceResult<TData> {
  /** Children whose fetch succeeded, in the order they were passed in. */
  results: ChildResource<TData>[];
  /**
   * Children whose fetch failed — in practice a 403 from a link that was
   * revoked between the children list and this request.
   */
  failed: UserPublic[];
  /** True while any child is still loading. */
  isPending: boolean;
  /** True only when every child failed; a partial failure is not an error. */
  isError: boolean;
}

/**
 * Fans a per-child endpoint out across all of a parent's children and merges
 * the responses.
 *
 * Parent views aggregate rather than switch between children, so every page
 * needs N requests reduced to one list. Partial success is the norm and is
 * surfaced as `failed` instead of collapsing the page: one revoked link should
 * not hide the siblings' data.
 *
 * The query key must match the corresponding single-child hook's key so both
 * read from the same cache entry.
 */
export function useChildrenResource<TData>(
  children: UserPublic[],
  queryKey: (child: UserPublic) => QueryKey,
  queryFn: (child: UserPublic) => Promise<TData>,
  enabled = true
): ChildrenResourceResult<TData> {
  return useQueries({
    queries: children.map((child) => ({
      queryKey: queryKey(child),
      queryFn: () => queryFn(child),
      enabled,
    })),
    combine: (queries) => {
      const results: ChildResource<TData>[] = [];
      const failed: UserPublic[] = [];

      queries.forEach((query, index) => {
        const child = children[index];
        if (!child) return;
        if (query.isError) {
          failed.push(child);
          return;
        }
        if (query.data !== undefined) {
          results.push({ child, data: query.data });
        }
      });

      return {
        results,
        failed,
        isPending: queries.some((query) => query.isPending),
        isError: queries.length > 0 && failed.length === queries.length,
      };
    },
  });
}
